import { useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { Drawer, toast } from '@vwt/ui';
import { useTranslation } from '../lib/i18n.js';
import { hasUnsavedChanges } from '../lib/unsaved.js';
import { WorkDetail } from '../features/work/work-detail.js';
import { RequestDetail } from '../features/requests/request-detail.js';

/** Search parameters that open a work item or a request on top of whatever page is showing. */
const PEEK_PARAMS = { work: 'peekWork', request: 'peekRequest' } as const;
type PeekKind = keyof typeof PEEK_PARAMS;

/**
 * Link target that opens an item in a drawer over the current page, so the person stays on the tab
 * they were working from. The work and requests pages keep their own `selected` drawer.
 */
export function usePeekHref() {
  const { pathname, search } = useLocation();
  return useCallback(
    (kind: PeekKind, id: string) => {
      const params = new URLSearchParams(search);
      for (const key of Object.values(PEEK_PARAMS)) params.delete(key);
      params.set(PEEK_PARAMS[kind], id);
      return `${pathname}?${params.toString()}`;
    },
    [pathname, search],
  );
}

/** Drawers for the peek parameters; rendered once by the app shell. */
export function PeekDrawers() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const workId = params.get(PEEK_PARAMS.work);
  const requestId = params.get(PEEK_PARAMS.request);
  const close = (kind: PeekKind) =>
    setParams((p) => {
      const next = new URLSearchParams(p);
      next.delete(PEEK_PARAMS[kind]);
      return next;
    });
  const onOpenChange = (kind: PeekKind) => (open: boolean) => {
    if (open) return;
    if (hasUnsavedChanges()) {
      toast.warning(t('common.unsavedChanges'));
      return;
    }
    close(kind);
  };
  const onEscapeKeyDown = (event: KeyboardEvent) => {
    if (!hasUnsavedChanges()) return;
    event.preventDefault();
    toast.warning(t('common.unsavedChanges'));
  };
  return (
    <>
      <Drawer open={!!workId} onOpenChange={onOpenChange('work')} onEscapeKeyDown={onEscapeKeyDown} title={t('work.title')} closeLabel={t('common.close')}>
        {workId ? <WorkDetail id={workId} inDrawer /> : null}
      </Drawer>
      <Drawer open={!!requestId} onOpenChange={onOpenChange('request')} onEscapeKeyDown={onEscapeKeyDown} title={t('requests.title')} closeLabel={t('common.close')}>
        {requestId ? <RequestDetail id={requestId} inDrawer /> : null}
      </Drawer>
    </>
  );
}
